import type { ServiceError } from '@ayumia/component-runtime';

/**
 * Client de l'API v1 — la seule source de données du navigateur (27 §27). Le tenant et l'acteur
 * viennent de la session au serveur ; le client ne les envoie jamais. Aucune route d'accès direct
 * à la base, aucun pipeline : commandes nommées, objets, requêtes nommées.
 */

export const API_PREFIX = '/api/v1';

export interface QueryEnvelope<T = unknown> {
  data: T;
  meta: {
    model_version?: string;
    permissions_applied?: { masked_fields?: string[]; restricted_fields?: string[]; record_filter_applied?: boolean };
    [key: string]: unknown;
  };
  page?: { next_cursor?: string; limit?: number; total?: number; totals?: ListTotal[]; groups?: ListGroup[]; groups_truncated?: boolean };
}

/** Groupe d'une lecture « groupes » (ADR-099) : la valeur telle que le filtre la reçoit (null : sans valeur), son nombre et ses totaux. */
export interface ListGroup {
  value: string | null;
  count: number;
  totals?: ListTotal[];
}

/** Total de pied servi par le serveur (ADR-095) : un sous-total par devise ou unité, en chaîne décimale exacte. */
export interface ListTotal {
  field_id: string;
  aggregation: 'sum' | 'avg' | 'min' | 'max' | 'count';
  values: Array<{ unit?: string; value: string; inputs: number }>;
  inputs: number;
  missing: number;
}

export interface CommandInput {
  payload: Record<string, unknown>;
  target?: { type: string; id: string };
  expected_version?: number;
  reason?: string;
  /** Fournie par l'appelant pour un rejeu sûr ; sinon une clé neuve par appel. */
  idempotency_key?: string;
}

export interface ListInput {
  /** `{ champ: { opérateur: valeur } }`, opérateurs gouvernés : eq, ne, in, gt, gte, lt, lte, contains, starts_with, exists. */
  filter?: Record<string, Record<string, string>>;
  sort?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  limit?: number;
  cursor?: string;
  /** Recherche (ADR-094) : le serveur échappe la saisie et ne cherche que sur les champs cherchables lisibles. */
  q?: string;
  q_fields?: readonly string[];
  /** Compteur total sous la même autorisation (`page.total`). */
  include_total?: boolean;
  /** Totaux de pied (ADR-095), calculés par le serveur sur tout l'ensemble filtré. */
  totals?: ReadonlyArray<{ field_id: string; aggregation: ListTotal['aggregation'] }>;
  /** Lecture « groupes » (ADR-099) : une entrée par valeur de ce champ, `data` vide. */
  group_by?: string;
  /** Sélecteur d'un formulaire de tâche (ADR-141, addendum (2)) : la liste est bornée à l'entité de la tâche, comme la commande. */
  work_item_id?: string;
}

export class ApiError extends Error implements ServiceError {
  constructor(
    readonly category: string,
    readonly code: string,
    message: string,
    readonly status: number,
    readonly fieldErrors: Array<{ field: string; code: string; message: string }> = [],
    /** Délai avant une nouvelle tentative, en secondes (en-tête `Retry-After` d'un 429). */
    readonly retryAfterSeconds?: number,
    /** Détails du refus (une décision de politique `require_step_up`, par exemple). */
    readonly details: Readonly<Record<string, unknown>> = {},
  ) {
    super(message);
  }

  toServiceError(): ServiceError {
    return { category: this.category, code: this.code, message: this.message };
  }
}

export type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export interface ApiClientOptions {
  /**
   * Jeton de session porté en `Bearer` : l'écran de développement seulement (ADR-091 §6), gardé en
   * mémoire, jamais dans le stockage du navigateur. Absent, la session est le cookie `HttpOnly`
   * posé par la connexion, et le jeton anti-CSRF accompagne toute requête qui écrit.
   */
  session?: () => string | null;
  fetch?: Fetch;
  baseUrl?: string;
  /** Appelé sur 401 : la session a expiré ou a été révoquée. */
  onUnauthenticated?: () => void;
}

/**
 * Jeton anti-CSRF de la session en cookie (ADR-091 §6), reçu dans l'en-tête `x-csrf-token` des
 * réponses de connexion et de `GET /auth/me`, gardé en mémoire et recopié dans l'en-tête de toute
 * requête qui écrit. Le script ne lit aucun cookie.
 */
let csrf: string | null = null;
export function csrfToken(): string | null {
  return csrf;
}
export function rememberCsrfToken(response: Response): void {
  const value = response.headers.get('x-csrf-token');
  if (value !== null && value !== '') csrf = value;
}
/** `Retry-After` en secondes (nombre ou date HTTP) ; absent ou illisible : `undefined`. */
export function retryAfterOf(response: Response): number | undefined {
  const raw = response.headers.get('retry-after');
  if (raw === null || raw.trim() === '') return undefined;
  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  const date = Date.parse(raw);
  return Number.isNaN(date) ? undefined : Math.max(0, Math.round((date - Date.now()) / 1000));
}

/** Corps JSON, ou `null` s'il est vide ou n'est pas du JSON (page d'erreur d'un relais). */
export function parseJsonBody(text: string): unknown {
  if (text === '') return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export function forgetCsrfToken(): void {
  csrf = null;
}

function newIdempotencyKey(): string {
  return `web-${crypto.randomUUID()}`;
}

export class ApiClient {
  private readonly fetchImpl: Fetch;

  constructor(private readonly options: ApiClientOptions) {
    this.fetchImpl = options.fetch ?? ((input, init) => fetch(input, init));
  }

  private async request<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' };
    const session = this.options.session?.() ?? null;
    if (session !== null) headers['authorization'] = `Bearer ${session}`;
    else if (method !== 'GET') {
      const csrf = csrfToken();
      if (csrf !== null) headers['x-csrf-token'] = csrf;
    }
    if (body !== undefined) headers['content-type'] = 'application/json';
    let response: Response;
    try {
      const init: RequestInit = { method, headers, credentials: 'same-origin' };
      if (body !== undefined) init.body = JSON.stringify(body);
      response = await this.fetchImpl(`${this.options.baseUrl ?? ''}${API_PREFIX}${path}`, init);
    } catch {
      throw new ApiError('DEPENDENCY_FAILURE', 'NETWORK_UNAVAILABLE', 'Le serveur est injoignable.', 0);
    }
    const text = await response.text();
    const parsed: unknown = text === '' ? null : JSON.parse(text);
    if (!response.ok) {
      if (response.status === 401) this.options.onUnauthenticated?.();
      const error = (parsed ?? {}) as { category?: string; code?: string; message?: string; field_errors?: ApiError['fieldErrors']; details?: Record<string, unknown> };
      throw new ApiError(error.category ?? 'INTERNAL_ERROR', error.code ?? `HTTP_${response.status}`, error.message ?? `Erreur ${response.status}.`, response.status, error.field_errors ?? [], undefined, error.details ?? {});
    }
    return parsed as T;
  }

  command<T = unknown>(name: string, input: CommandInput): Promise<T> {
    const body: Record<string, unknown> = { payload: input.payload, idempotency_key: input.idempotency_key ?? newIdempotencyKey() };
    if (input.target !== undefined) body['target'] = input.target;
    if (input.expected_version !== undefined) body['expected_version'] = input.expected_version;
    if (input.reason !== undefined) body['reason'] = input.reason;
    return this.request<T>('POST', `/commands/${encodeURIComponent(name)}`, body);
  }

  query<T = unknown>(name: string, parameters: Readonly<Record<string, string>> = {}): Promise<QueryEnvelope<T>> {
    const search = new URLSearchParams(parameters).toString();
    return this.request<QueryEnvelope<T>>('GET', `/queries/${encodeURIComponent(name)}${search === '' ? '' : `?${search}`}`);
  }

  /** Requête nommée dont les paramètres sont volumineux (brouillon d'un gabarit) : POST, lecture seule, même autorisation. */
  queryPost<T = unknown>(name: string, parameters: Readonly<Record<string, string>> = {}): Promise<QueryEnvelope<T>> {
    return this.request<QueryEnvelope<T>>('POST', `/queries/${encodeURIComponent(name)}`, { parameters });
  }

  getObject<T = Record<string, unknown>>(type: string, id: string): Promise<QueryEnvelope<T>> {
    return this.request<QueryEnvelope<T>>('GET', `/objects/${encodeURIComponent(type)}/${encodeURIComponent(id)}`);
  }

  listObjects<T = Record<string, unknown>>(type: string, input: ListInput = {}): Promise<QueryEnvelope<T[]>> {
    return this.request<QueryEnvelope<T[]>>('GET', `/objects/${encodeURIComponent(type)}${listSearch(input)}`);
  }

  /**
   * Dépôt d'un fichier sur l'URL signée que le serveur a délivrée (ADR-054) : aucun jeton de
   * session ne part vers le stockage, l'URL porte seule le droit, pour ce fichier et peu de temps.
   */
  async upload(signed: { url: string; method: string; headers?: Record<string, string> }, body: Blob): Promise<void> {
    let response: Response;
    try {
      response = await this.fetchImpl(signed.url, { method: signed.method, headers: signed.headers ?? {}, body });
    } catch {
      // Le navigateur ne peut pas lire la réponse (`TypeError` de `fetch`) : échec réseau, ou origine
      // refusée par la règle CORS du compartiment (préflight 403 ou 503). Ce n'est pas une panne avérée
      // du stockage : on dit ce que l'on sait, et qui prévenir.
      throw new ApiError('DEPENDENCY_FAILURE', 'UPLOAD_NOT_ACCEPTED', 'Le dépôt a échoué : le stockage n’a pas accepté l’envoi depuis cette adresse. Prévenez l’administrateur.', 0);
    }
    // Une vraie réponse 5xx, lisible : le stockage est indisponible.
    if (response.status >= 500) throw new ApiError('DEPENDENCY_FAILURE', 'STORAGE_UNAVAILABLE', `Le stockage de fichiers est indisponible (${response.status}). Réessayez plus tard.`, response.status);
    if (!response.ok) throw new ApiError('DEPENDENCY_FAILURE', 'UPLOAD_FAILED', `Le dépôt du fichier a échoué (${response.status}).`, response.status);
  }
}

/** `filter[champ][op]=valeur`, `sort=-champ`, `limit`, `cursor`, `q`, `q_fields`, `include_total`, `totals`, `group_by` : la syntaxe gouvernée de l'API. */
export function listSearch(input: ListInput): string {
  const search = new URLSearchParams();
  for (const [field, operators] of Object.entries(input.filter ?? {})) {
    for (const [operator, value] of Object.entries(operators)) if (value !== '') search.append(`filter[${field}][${operator}]`, value);
  }
  if (input.sort !== undefined && input.sort.length > 0) search.set('sort', input.sort.map((entry) => `${entry.direction === 'desc' ? '-' : ''}${entry.field}`).join(','));
  if (input.limit !== undefined) search.set('limit', String(input.limit));
  if (input.cursor !== undefined) search.set('cursor', input.cursor);
  if (input.q !== undefined && input.q.trim() !== '') search.set('q', input.q.trim());
  if (input.q_fields !== undefined && input.q_fields.length > 0 && input.q !== undefined && input.q.trim() !== '') search.set('q_fields', input.q_fields.join(','));
  if (input.include_total === true) search.set('include_total', 'true');
  if (input.totals !== undefined && input.totals.length > 0) search.set('totals', input.totals.map((total) => `${total.aggregation}:${total.field_id}`).join(','));
  if (input.group_by !== undefined) search.set('group_by', input.group_by);
  if (input.work_item_id !== undefined && input.work_item_id !== '') search.set('work_item_id', input.work_item_id);
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

/** Refus d'une action qui exige une vérification récente par code (décision de politique `require_step_up`). */
export function needsStepUp(failure: unknown): boolean {
  return failure instanceof ApiError && failure.details['decision'] === 'require_step_up';
}
